/**
 * Every user-facing string the AI Builder chat can emit, server side and front
 * side alike. One file, so this IS the exhaustive Tolgee list: a message
 * declared anywhere else escapes translation silently.
 *
 * These are Tolgee i18n keys with {param} placeholders; the frontend translates
 * them via t(key, params). Prop/step display names arrive as params and are
 * translated separately via the piece's Tolgee namespace.
 *
 * Not for prompt fragments sent to the model (English, never shown, never
 * translated) — those belong with the prompt doctrine.
 */

/** Step-by-step configuration wizard (orchestrator). */
export const AiBuilderMessageKey = {
    /** Multi-select or unresolved options: the panel, open on the field, shows what to pick */
    selectDropdown: 'Make your choice for **{step}** in the panel.',
    selectButton: '{prop} selected',
    authButton: '{piece} account connected',
    refreshDynamic: 'Reload the fields of **{step}** (🔄 icon).',
    dynamicNoFields:
        '**{step}** has no fields to fill: its source looks empty. Complete it, then click Done.',
    refreshButton: 'Fields refreshed',
    stepUpdated: 'I updated {fields} for **{step}**.',
    listValuesRemoved: 'I removed {values} from **{field}** in **{step}**.',
    // What an AI write changed, read from the persisted flow (KHB-622)
    stepAdded: 'I added **{step}**.',
    stepRemoved: 'I removed **{step}**.',
    triggerSet: 'The flow now starts with **{step}**.',
    stepRenamed: 'I renamed **{before}** to **{after}**.',
    /** A loop, a router, or a step whose field labels are unknown: never a prop key */
    stepChanged: 'I changed **{step}**.',
    /** The model called a construction tool and the flow is as it was */
    nothingChanged: 'I did not change anything in the flow.',
    /** A debug turn changed the flow: the user's test is what proves the fix */
    changesAppliedRetest: 'Run the test again to check.',
    // Field names come after a colon: no article to agree with a label whose
    // gender nobody knows
    skippedFields:
        'Left empty in **{step}**, no matching data found: {fields}. Fill in the panel if needed.',
    skippedNotRequested:
        'Left empty in **{step}**, not mentioned in your request: {fields}. Ask me if needed.',
    // An open question says every way it can be answered: the Done button
    // under it re-walks the step after a panel fill
    askRequiredValue:
        'What should I use as {fields} for **{step}**? Answer here, or fill it in the panel and click Done.',
    /** The model's own question, in the user's language, framed with the panel route */
    askRequiredValuePhrased:
        '{question}\n\nYou can also fill it in the panel, then click Done.',
    /** Fields were found but none matched the data: the step would run empty */
    dynamicNothingToWrite:
        'Nothing I found fits {fields} in **{step}**, so it would run without any data. Tell me here what to put there, or fill it in the panel and click Done.',
    /** The choice card's ask depends on what the user named: matched, not found, or nothing */
    chooseOptionMatched:
        'Several choices match "**{hint}**": which one do you want?',
    // The fallbacks when the card's question cannot be phrased: no field
    // label, which is often jargon or untranslated, and no article to agree
    chooseOptionNoMatch:
        'I could not find "**{hint}**": make your choice for **{step}**.',
    /** "Not found" stays code, before the question the model phrased */
    chooseOptionNoMatchPhrased:
        'I could not find "**{hint}**". {question}',
    chooseOptionNoHint: 'Make your choice for **{step}**.',
    connectAccount: 'Connect a **{piece}** account to continue.',
    chooseAccount: 'Which **{piece}** account should this step use?',
    writePreview:
        'Step **{step}** is about to run **{action}** with:\n\n{values}\n\nRun the test?',
    /** Same text without the action: a step keeps the action's name by default */
    writePreviewSameName:
        '**{step}** is about to run with:\n\n{values}\n\nRun the test?',
    writePreviewNoReader:
        'Step **{step}** will run **{action}** with:\n\n{values}',
    writePreviewNoReaderSameName:
        '**{step}** will run with:\n\n{values}',
    writeTestButton: 'Run the test',
    writeSkipButton: 'Skip the test',
    writeSkippedDownstream:
        '**{step}** was not tested: the next steps may need adjusting after a first real run.',
    stepTestFailed: 'The test of **{step}** failed: {error}',
    triggerMockReady:
        'The form has no submission yet: I am testing with sample values.',
    triggerKizeoFormsNoSubmissions:
        'This form has no submission yet: submit one from Kizeo Forms, then confirm here.',
    triggerSimulationNeeded:
        'I cannot test **{step}** myself: it waits for a real incoming event, so only a real one produces data. Start its test in the panel, make the event happen (send the request, the message…), then confirm here.',
    loopNoListFound:
        'I found no list to loop through for **{step}**. Tell me here what to loop over, or test the trigger again on data that has a list, then click Done.',
    loopListEmpty:
        'The list to loop through in **{step}** is empty: test again with at least one item.',
    /** Above the choice card when the model could not tell which list the loop walks */
    loopChooseList: 'Which list should **{step}** loop through?',
    /** The panel refuses to test it, while the engine would read the empty value as 0 */
    routerConditionIncomplete:
        'A condition of **{step}** is incomplete. Tell me here what it should check, or complete it in the panel, then click Done.',
    validationIssuesFound: 'Fix these points to finish.',
    /** One line per blocking issue, by validator code — the technical detail goes to the model only */
    validationIssue: {
        missing_required: '**{field}** is empty in **{step}**.',
        unresolved_dropdown:
            '**{field}** in **{step}** is waiting for its options to load.',
        invalid_static_option:
            'The value of **{field}** in **{step}** is not in its list.',
        broken_step_ref:
            '**{step}** reads data from a step that no longer exists.',
        forward_step_ref:
            '**{step}** reads data from a step that runs after it.',
        unresolved_data_ref:
            '**{field}** in **{step}** points at data that does not exist.',
        ref_resolves_to_object:
            '**{field}** in **{step}** points at a group of values, not a single one.',
        empty_branch:
            'A branch of **{step}** is missing its condition or its steps.',
        incomplete_condition:
            '**{field}** of **{step}** has a condition with no value to compare.',
        unknown_key: '**{step}** has a setting that no longer exists.',
        unused_output: '**{step}** computes a value that no later step uses.',
    },
    testStep: 'Test step **{step}** in the panel and confirm.',
    testButton: '{step} tested',
    retryTestButton: 'Retry the test',
    fixItButton: 'Fix the issues for me',
    configureDoneButton: 'Done ✓',
    configureHelpButton: 'I need help',
    debugFixedButton: 'Fixed ✓',
    debugHelpButton: 'I don\'t understand',
    fillCredential:
        '**{step}** needs a secret key: paste it into {fields} under **{prop}** in the panel, then click Done.',
    credentialFilledButton: 'Credential filled',
    flowNamed: 'I named the flow **{name}** — rename it in the header if you prefer.',
}

/** Turn-level outcomes: a guard fired, the turn was refused or lost. */
export const AiBuilderSystemMessage = {
    messageTooLong:
        'Message too long ({length} chars). Maximum is {max} characters.',
    budgetStop:
        'I paused before finishing. Send a message and I will continue from here.',
    goingInCircles: 'I seem to be going in circles. Here is the flow so far.',
    technicalIssue:
        'Sorry, I encountered a technical issue. Here is the flow as-is.',
    flowBusy:
        'A generation is already running on this flow — wait for it to finish before sending another message.',
    // No promised duration: the hourly window is a fixed UTC hour — at five
    // past, "a few minutes" would mean fifty-five
    hourlyLimitReached:
        'The AI assistant is receiving too many requests from your account right now. Please try again later.',
    dailyLimitReached:
        'The AI assistant reached its daily usage limit for your account. It will be available again tomorrow.',
    turnFailed: 'No answer was received for this message. Send it again to retry.',
    // Never retryable: the turn changed the flow, replaying it would apply the change twice
    changesApplied: 'Done: the flow is updated.',
    generationFailed:
        'An error occurred while generating the flow. Please try again.',
}

/**
 * The turns a retry can legitimately replay: the ones that FAILED. Elsewhere a
 * turn has side effects (it builds steps, runs tests), so re-rolling it is not
 * "regenerating an answer" — the user has free text, the wizard buttons and
 * undo for that.
 */
export const RETRYABLE_SYSTEM_MESSAGES: string[] = [
    AiBuilderSystemMessage.turnFailed,
    AiBuilderSystemMessage.generationFailed,
    AiBuilderSystemMessage.technicalIssue,
    AiBuilderSystemMessage.goingInCircles,
]
